import { call, put, takeLatest } from 'redux-saga/effects'
import * as types from '../../../actions/index.types'
import * as req from '../../../../axios/request/SocietyPortal/TaskReqs/TaskReqs'

// Generic worker factory.
// `carry`: 'result' passes the whole envelope (lists need meta.pagination),
//          'data' passes result.data, otherwise no payload.
const makeWorker = (reqFn, successType, failureType, carry = 'data') =>
    function* (action) {
        try {
            const result = yield call(reqFn, action);
            if (result?.success === true) {
                const payload = carry === 'result' ? result : (carry === 'data' ? result.data : undefined);
                yield put({ type: successType, payload });
                action.data?.onSuccess?.(result);
            } else {
                yield put({ type: failureType, message: result?.response?.data?.message || result?.message });
                action.data?.onFailure?.(result);
            }
        } catch (e) {
            console.log(e);
            yield put({ type: failureType, message: e?.message });
            action.data?.onFailure?.(e);
        }
    };

// ============ TASKS ============
const getTasksList = makeWorker(req.getTasksListReq, types.SOCIETY_GET_TASKS_LIST_SUCCESS, types.SOCIETY_GET_TASKS_LIST_FAILURE, 'result');
const createTask = makeWorker(req.createTaskReq, types.SOCIETY_CREATE_TASK_SUCCESS, types.SOCIETY_CREATE_TASK_FAILURE);
const getTaskDetails = makeWorker(req.getTaskDetailsReq, types.SOCIETY_GET_TASK_DETAILS_SUCCESS, types.SOCIETY_GET_TASK_DETAILS_FAILURE);
const updateTask = makeWorker(req.updateTaskReq, types.SOCIETY_UPDATE_TASK_SUCCESS, types.SOCIETY_UPDATE_TASK_FAILURE);
const deleteTask = makeWorker(req.deleteTaskReq, types.SOCIETY_DELETE_TASK_SUCCESS, types.SOCIETY_DELETE_TASK_FAILURE);
const cloneTask = makeWorker(req.cloneTaskReq, types.SOCIETY_CLONE_TASK_SUCCESS, types.SOCIETY_CLONE_TASK_FAILURE);
const getTaskHistory = makeWorker(req.getTaskHistoryReq, types.SOCIETY_GET_TASK_HISTORY_SUCCESS, types.SOCIETY_GET_TASK_HISTORY_FAILURE);

// ============ WORKFLOW ============
const transitionTask = makeWorker(req.transitionTaskReq, types.SOCIETY_TRANSITION_TASK_SUCCESS, types.SOCIETY_TRANSITION_TASK_FAILURE);
const changeTaskPriority = makeWorker(req.changeTaskPriorityReq, types.SOCIETY_CHANGE_TASK_PRIORITY_SUCCESS, types.SOCIETY_CHANGE_TASK_PRIORITY_FAILURE);
const changeTaskTitle = makeWorker(req.changeTaskTitleReq, types.SOCIETY_CHANGE_TASK_TITLE_SUCCESS, types.SOCIETY_CHANGE_TASK_TITLE_FAILURE);

// ============ ASSIGNMENT ============
const assignTask = makeWorker(req.assignTaskReq, types.SOCIETY_ASSIGN_TASK_SUCCESS, types.SOCIETY_ASSIGN_TASK_FAILURE);
const selfAssignTask = makeWorker(req.selfAssignTaskReq, types.SOCIETY_SELF_ASSIGN_TASK_SUCCESS, types.SOCIETY_SELF_ASSIGN_TASK_FAILURE);
const reassignTask = makeWorker(req.reassignTaskReq, types.SOCIETY_REASSIGN_TASK_SUCCESS, types.SOCIETY_REASSIGN_TASK_FAILURE);
const delegateTask = makeWorker(req.delegateTaskReq, types.SOCIETY_DELEGATE_TASK_SUCCESS, types.SOCIETY_DELEGATE_TASK_FAILURE);
const removeAssignee = makeWorker(req.removeAssigneeReq, types.SOCIETY_REMOVE_TASK_ASSIGNEE_SUCCESS, types.SOCIETY_REMOVE_TASK_ASSIGNEE_FAILURE);

// ============ WATCHERS ============
const addWatcher = makeWorker(req.addWatcherReq, types.SOCIETY_ADD_TASK_WATCHER_SUCCESS, types.SOCIETY_ADD_TASK_WATCHER_FAILURE);
const removeWatcher = makeWorker(req.removeWatcherReq, types.SOCIETY_REMOVE_TASK_WATCHER_SUCCESS, types.SOCIETY_REMOVE_TASK_WATCHER_FAILURE);

// ============ COMMENTS ============
const getComments = makeWorker(req.getCommentsReq, types.SOCIETY_GET_TASK_COMMENTS_SUCCESS, types.SOCIETY_GET_TASK_COMMENTS_FAILURE);
const addComment = makeWorker(req.addCommentReq, types.SOCIETY_ADD_TASK_COMMENT_SUCCESS, types.SOCIETY_ADD_TASK_COMMENT_FAILURE);
const uploadCommentAttachments = makeWorker(req.uploadCommentAttachmentsReq, types.SOCIETY_UPLOAD_TASK_COMMENT_ATTACHMENTS_SUCCESS, types.SOCIETY_UPLOAD_TASK_COMMENT_ATTACHMENTS_FAILURE);

// ============ CHECKLIST ============
const addChecklistItem = makeWorker(req.addChecklistItemReq, types.SOCIETY_ADD_TASK_CHECKLIST_SUCCESS, types.SOCIETY_ADD_TASK_CHECKLIST_FAILURE);
const toggleChecklistItem = makeWorker(req.toggleChecklistItemReq, types.SOCIETY_TOGGLE_TASK_CHECKLIST_SUCCESS, types.SOCIETY_TOGGLE_TASK_CHECKLIST_FAILURE);
const deleteChecklistItem = makeWorker(req.deleteChecklistItemReq, types.SOCIETY_DELETE_TASK_CHECKLIST_SUCCESS, types.SOCIETY_DELETE_TASK_CHECKLIST_FAILURE);

// ============ DEPENDENCIES ============
const addDependency = makeWorker(req.addDependencyReq, types.SOCIETY_ADD_TASK_DEPENDENCY_SUCCESS, types.SOCIETY_ADD_TASK_DEPENDENCY_FAILURE);
const removeDependency = makeWorker(req.removeDependencyReq, types.SOCIETY_REMOVE_TASK_DEPENDENCY_SUCCESS, types.SOCIETY_REMOVE_TASK_DEPENDENCY_FAILURE);

// ============ ATTACHMENTS ============
const uploadAttachment = makeWorker(req.uploadAttachmentReq, types.SOCIETY_UPLOAD_TASK_ATTACHMENT_SUCCESS, types.SOCIETY_UPLOAD_TASK_ATTACHMENT_FAILURE);
const deleteAttachment = makeWorker(req.deleteAttachmentReq, types.SOCIETY_DELETE_TASK_ATTACHMENT_SUCCESS, types.SOCIETY_DELETE_TASK_ATTACHMENT_FAILURE);

// ============ PROJECTS ============
const getProjectsList = makeWorker(req.getProjectsListReq, types.SOCIETY_GET_TASK_PROJECTS_SUCCESS, types.SOCIETY_GET_TASK_PROJECTS_FAILURE, 'result');
const createProject = makeWorker(req.createProjectReq, types.SOCIETY_CREATE_TASK_PROJECT_SUCCESS, types.SOCIETY_CREATE_TASK_PROJECT_FAILURE);
const getProjectDetails = makeWorker(req.getProjectDetailsReq, types.SOCIETY_GET_TASK_PROJECT_DETAILS_SUCCESS, types.SOCIETY_GET_TASK_PROJECT_DETAILS_FAILURE);
const updateProject = makeWorker(req.updateProjectReq, types.SOCIETY_UPDATE_TASK_PROJECT_SUCCESS, types.SOCIETY_UPDATE_TASK_PROJECT_FAILURE);
const deleteProject = makeWorker(req.deleteProjectReq, types.SOCIETY_DELETE_TASK_PROJECT_SUCCESS, types.SOCIETY_DELETE_TASK_PROJECT_FAILURE);

// ============ COLLECTION ============
const bulkCreateTasks = makeWorker(req.bulkCreateTasksReq, types.SOCIETY_BULK_CREATE_TASKS_SUCCESS, types.SOCIETY_BULK_CREATE_TASKS_FAILURE);
const getTaskDashboard = makeWorker(req.getTaskDashboardReq, types.SOCIETY_GET_TASK_DASHBOARD_SUCCESS, types.SOCIETY_GET_TASK_DASHBOARD_FAILURE);

export default function* taskSaga() {
    yield takeLatest(types.SOCIETY_GET_TASKS_LIST, getTasksList);
    yield takeLatest(types.SOCIETY_CREATE_TASK, createTask);
    yield takeLatest(types.SOCIETY_GET_TASK_DETAILS, getTaskDetails);
    yield takeLatest(types.SOCIETY_UPDATE_TASK, updateTask);
    yield takeLatest(types.SOCIETY_DELETE_TASK, deleteTask);
    yield takeLatest(types.SOCIETY_CLONE_TASK, cloneTask);
    yield takeLatest(types.SOCIETY_GET_TASK_HISTORY, getTaskHistory);
    yield takeLatest(types.SOCIETY_TRANSITION_TASK, transitionTask);
    yield takeLatest(types.SOCIETY_CHANGE_TASK_PRIORITY, changeTaskPriority);
    yield takeLatest(types.SOCIETY_CHANGE_TASK_TITLE, changeTaskTitle);
    yield takeLatest(types.SOCIETY_ASSIGN_TASK, assignTask);
    yield takeLatest(types.SOCIETY_SELF_ASSIGN_TASK, selfAssignTask);
    yield takeLatest(types.SOCIETY_REASSIGN_TASK, reassignTask);
    yield takeLatest(types.SOCIETY_DELEGATE_TASK, delegateTask);
    yield takeLatest(types.SOCIETY_REMOVE_TASK_ASSIGNEE, removeAssignee);
    yield takeLatest(types.SOCIETY_ADD_TASK_WATCHER, addWatcher);
    yield takeLatest(types.SOCIETY_REMOVE_TASK_WATCHER, removeWatcher);
    yield takeLatest(types.SOCIETY_GET_TASK_COMMENTS, getComments);
    yield takeLatest(types.SOCIETY_ADD_TASK_COMMENT, addComment);
    yield takeLatest(types.SOCIETY_UPLOAD_TASK_COMMENT_ATTACHMENTS, uploadCommentAttachments);
    yield takeLatest(types.SOCIETY_ADD_TASK_CHECKLIST, addChecklistItem);
    yield takeLatest(types.SOCIETY_TOGGLE_TASK_CHECKLIST, toggleChecklistItem);
    yield takeLatest(types.SOCIETY_DELETE_TASK_CHECKLIST, deleteChecklistItem);
    yield takeLatest(types.SOCIETY_ADD_TASK_DEPENDENCY, addDependency);
    yield takeLatest(types.SOCIETY_REMOVE_TASK_DEPENDENCY, removeDependency);
    yield takeLatest(types.SOCIETY_UPLOAD_TASK_ATTACHMENT, uploadAttachment);
    yield takeLatest(types.SOCIETY_DELETE_TASK_ATTACHMENT, deleteAttachment);
    yield takeLatest(types.SOCIETY_GET_TASK_PROJECTS, getProjectsList);
    yield takeLatest(types.SOCIETY_CREATE_TASK_PROJECT, createProject);
    yield takeLatest(types.SOCIETY_GET_TASK_PROJECT_DETAILS, getProjectDetails);
    yield takeLatest(types.SOCIETY_UPDATE_TASK_PROJECT, updateProject);
    yield takeLatest(types.SOCIETY_DELETE_TASK_PROJECT, deleteProject);
    yield takeLatest(types.SOCIETY_BULK_CREATE_TASKS, bulkCreateTasks);
    yield takeLatest(types.SOCIETY_GET_TASK_DASHBOARD, getTaskDashboard);
}
